import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {ThemeProvider as MuiThemeProvider, createTheme} from '@mui/material';

import {darkPalette, lightPalette} from './palette';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'themeMode';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Пока на токены переведена только часть экранов, тёмную тему по умолчанию
 * не включаем: на немигрированных страницах фон остаётся светлым, а текст
 * становится светлым — читать невозможно.
 * Когда миграция закончится — поменять на 'system' (и в public/index.html).
 */
const DEFAULT_MODE: ThemeMode = 'light';

interface ThemeModeContextValue {
  /** что выбрал пользователь */
  mode: ThemeMode;
  /** что реально показываем (system уже разрешён) */
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: 'system',
  resolved: 'light',
  setMode: () => undefined,
});

export const useThemeMode = () => useContext(ThemeModeContext);

const readStoredMode = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    // localStorage может быть недоступен (приватный режим)
  }
  return DEFAULT_MODE;
};

const getSystemPrefersDark = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(DARK_QUERY).matches;

export const ThemeModeProvider: FC<{children: ReactNode}> = ({children}) => {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [systemPrefersDark, setSystemPrefersDark] =
    useState<boolean>(getSystemPrefersDark);

  // следим за системной темой — режим 'system' должен переключаться на лету
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) =>
      setSystemPrefersDark(event.matches);

    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const resolved: ResolvedTheme =
    mode === 'system' ? (systemPrefersDark ? 'dark' : 'light') : mode;

  // data-theme на <html> переключает CSS-токены (см. styles/_tokens.scss)
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', resolved);
    // чтобы нативные скроллбары и контролы тоже стали тёмными
    root.style.colorScheme = resolved;
  }, [resolved]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // не критично — тема просто не переживёт перезагрузку
    }
  }, []);

  const muiTheme = useMemo(() => {
    const isDark = resolved === 'dark';
    const p = isDark ? darkPalette : lightPalette;
    return createTheme({
      palette: {
        mode: resolved,
        primary: {main: p.primary, dark: p.primaryHover},
        error: {main: p.danger},
        success: {main: p.success},
        background: {default: p.bgApp, paper: p.bgSurface},
        text: {primary: p.textPrimary, secondary: p.textSecondary},
        divider: p.border,
      },
      typography: {fontFamily: "'Poppins', sans-serif"},
      components: {
        MuiPaper: {
          styleOverrides: {
            root: {backgroundImage: 'none'},
          },
        },
        // Дефолтный тултип MUI — почти чёрный, на тёмном фоне сливается.
        // Делаем его приподнятой поверхностью с рамкой, чтобы был заметен.
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              backgroundColor: isDark ? p.bgElevated : '#2B3040',
              color: isDark ? p.textPrimary : '#FFFFFF',
              border: `1px solid ${isDark ? p.border : 'transparent'}`,
              fontFamily: "'Poppins', sans-serif",
              fontSize: '12px',
              lineHeight: '18px',
              padding: '6px 10px',
              borderRadius: '8px',
              boxShadow: isDark
                ? '0 6px 20px rgba(0, 0, 0, 0.55)'
                : '0 6px 20px rgba(0, 0, 0, 0.25)',
            },
            arrow: {
              color: isDark ? p.bgElevated : '#2B3040',
              '&::before': {
                border: `1px solid ${isDark ? p.border : 'transparent'}`,
              },
            },
          },
        },
      },
    });
  }, [resolved]);

  const value = useMemo(
    () => ({mode, resolved, setMode}),
    [mode, resolved, setMode],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export default ThemeModeProvider;
